import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, LogOut, BedDouble, Inbox, BarChart3, Phone, Calendar, Users, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout, authHeaders } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [stats, setStats] = useState({ total_rooms: 0, active_rooms: 0, total_inquiries: 0, new_inquiries: 0 });

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { headers: authHeaders }).then((r) => setStats(r.data)).catch(() => {});
  }, [authHeaders, tab]);

  const doLogout = () => { logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-cream-100 flex" data-testid="admin-dashboard">
      <aside className="w-64 bg-white border-r border-forest-100 flex flex-col p-6 sticky top-0 h-screen">
        <Link to="/" className="flex items-center gap-2 mb-10" data-testid="admin-logo">
          <svg width="28" height="28" viewBox="0 0 40 40" fill="none">
            <path d="M20 2 L36 12 V28 L20 38 L4 28 V12 Z" stroke="#1B3022" strokeWidth="1.5" />
            <path d="M12 26 L18 18 L22 22 L28 14" stroke="#B8860B" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div className="leading-none">
            <div className="font-display text-base text-forest-700">Naljuri Inn</div>
            <div className="text-[9px] uppercase tracking-widest2 text-gold-600 mt-0.5">Admin</div>
          </div>
        </Link>

        <nav className="space-y-1 flex-1">
          <SideBtn active={tab==='inquiries'} onClick={() => setTab('inquiries')} icon={Inbox} label="Inquiries" badge={stats.new_inquiries} testid="tab-inquiries" />
          <SideBtn active={tab==='rooms'} onClick={() => setTab('rooms')} icon={BedDouble} label="Rooms" testid="tab-rooms" />
          <SideBtn active={tab==='overview'} onClick={() => setTab('overview')} icon={BarChart3} label="Overview" testid="tab-overview" />
        </nav>

        <div className="border-t border-forest-100 pt-6">
          <div className="text-[10px] uppercase tracking-widest2 text-gold-600">Signed in</div>
          <div className="text-sm text-forest-700 mt-1 truncate">{user?.email}</div>
          <button onClick={doLogout} data-testid="admin-logout"
                  className="mt-4 inline-flex items-center gap-2 text-xs text-slate-warm hover:text-forest-800">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-8 md:p-12">
        {tab === 'overview' && <Overview stats={stats} />}
        {tab === 'rooms' && <RoomsAdmin authHeaders={authHeaders} />}
        {tab === 'inquiries' && <InquiriesAdmin authHeaders={authHeaders} />}
      </main>
    </div>
  );
}

const SideBtn = ({ active, onClick, icon: Icon, label, badge, testid }) => (
  <button onClick={onClick} data-testid={testid}
          className={`w-full flex items-center justify-between px-4 py-2.5 rounded-md text-sm transition-colors ${active ? 'bg-forest-700 text-cream-100' : 'text-forest-700 hover:bg-cream-200'}`}>
    <span className="flex items-center gap-3"><Icon className="w-4 h-4" /> {label}</span>
    {badge ? <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-gold-600 text-cream-100' : 'bg-gold-600 text-cream-100'}`}>{badge}</span> : null}
  </button>
);

const Overview = ({ stats }) => (
  <div data-testid="overview-panel">
    <h1 className="text-3xl font-display text-forest-700">Overview</h1>
    <p className="text-sm text-slate-warm mt-2">A quick look at the property pulse.</p>
    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      <Stat label="New inquiries" value={stats.new_inquiries} accent />
      <Stat label="Total inquiries" value={stats.total_inquiries} />
      <Stat label="Active rooms" value={stats.active_rooms} />
      <Stat label="Total rooms" value={stats.total_rooms} />
    </div>
  </div>
);

const Stat = ({ label, value, accent }) => (
  <div className={`p-7 border rounded-md ${accent ? 'bg-forest-700 text-cream-100 border-forest-700' : 'bg-white border-forest-100 text-forest-700'}`}>
    <div className={`text-[10px] uppercase tracking-widest2 ${accent ? 'text-gold-400' : 'text-gold-600'}`}>{label}</div>
    <div className="mt-3 text-4xl font-display">{value}</div>
  </div>
);

// ---------- Inquiries ----------
function InquiriesAdmin({ authHeaders }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inquiries`, { headers: authHeaders });
      setItems(r.data);
    } catch { toast.error("Could not load inquiries"); }
    setLoading(false);
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/inquiries/${id}`, { status }, { headers: authHeaders });
      toast.success("Updated");
      load();
    } catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try { await axios.delete(`${API}/inquiries/${id}`, { headers: authHeaders }); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="inquiries-panel">
      <h1 className="text-3xl font-display text-forest-700">Inquiries</h1>
      <p className="text-sm text-slate-warm mt-2">Reservation requests sent through the website.</p>

      <div className="mt-10 bg-white border border-forest-100 rounded-md overflow-hidden">
        {(() => {
          if (loading) {
            return (
              <div className="p-12 flex items-center justify-center text-slate-warm">
                <Loader2 className="w-5 h-5 animate-spin"/>
              </div>
            );
          }
          if (items.length === 0) {
            return <div className="p-12 text-center text-slate-warm text-sm">No inquiries yet.</div>;
          }
          return (
          <div className="divide-y divide-forest-100">
            {items.map((i) => (
              <div key={i.id} className="p-6 grid md:grid-cols-12 gap-4 items-start" data-testid={`inquiry-${i.id}`}>
                <div className="md:col-span-4">
                  <div className="text-[10px] uppercase tracking-widest2 text-gold-600">{new Date(i.created_at).toLocaleString()}</div>
                  <div className="mt-1 text-base text-forest-700 font-medium">{i.name}</div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-warm"><Phone className="w-3 h-3"/> {i.phone}</div>
                  {i.email && <div className="text-xs text-slate-warm">{i.email}</div>}
                </div>
                <div className="md:col-span-4 text-sm text-slate-warm space-y-1.5">
                  <div className="flex items-center gap-2"><Calendar className="w-3 h-3 text-gold-600"/> {i.check_in} → {i.check_out}</div>
                  <div className="flex items-center gap-2"><Users className="w-3 h-3 text-gold-600"/> {i.guests} guests</div>
                  <div className="text-forest-700">{i.room_name || 'Any suite'}</div>
                  {i.message && <div className="italic text-xs mt-2">"{i.message}"</div>}
                </div>
                <div className="md:col-span-4 flex flex-col gap-2 items-start md:items-end">
                  <select value={i.status} onChange={(e) => setStatus(i.id, e.target.value)}
                          data-testid={`inquiry-status-${i.id}`}
                          className={`text-xs px-3 py-1.5 rounded-full border ${statusPillClass(i.status)}`}>
                    <option value="new">new</option>
                    <option value="contacted">contacted</option>
                    <option value="confirmed">confirmed</option>
                    <option value="closed">closed</option>
                  </select>
                  <a href={`https://wa.me/${i.phone.replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer"
                     className="text-xs text-gold-600 hover:underline">Open WhatsApp →</a>
                  <button onClick={() => remove(i.id)} className="text-xs text-red-600 hover:underline">Delete</button>
                </div>
              </div>
            ))}
          </div>
          );
        })()}
      </div>
    </div>
  );
}

// ---------- Rooms admin ----------
function RoomsAdmin({ authHeaders }) {
  const [rooms, setRooms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/rooms/all`, { headers: authHeaders });
      setRooms(r.data);
    } catch { toast.error("Could not load rooms"); }
    setLoading(false);
  }, [authHeaders]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this room?")) return;
    try { await axios.delete(`${API}/rooms/${id}`, { headers: authHeaders }); load(); } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="rooms-panel">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display text-forest-700">Rooms</h1>
          <p className="text-sm text-slate-warm mt-2">Manage suite inventory shown on the public site.</p>
        </div>
        <button onClick={() => setEditing({})} data-testid="add-room-btn"
                className="inline-flex items-center gap-2 bg-forest-700 text-cream-100 px-5 py-2.5 rounded-full text-sm hover:bg-forest-800 border border-gold-600/40">
          <Plus className="w-4 h-4" /> New room
        </button>
      </div>

      <div className="mt-10 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        {loading ? <div className="text-slate-warm"><Loader2 className="w-5 h-5 animate-spin"/></div> :
          rooms.map((r) => (
          <div key={r.id} className="bg-white border border-forest-100 rounded-md overflow-hidden flex flex-col" data-testid={`admin-room-${r.id}`}>
            <img src={r.image_url} alt={r.name_en} className="h-44 w-full object-cover" />
            <div className="p-5 flex flex-col flex-1">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-base font-display text-forest-700">{r.name_en}</div>
                  <div className="text-xs text-slate-warm">
                    ৳{r.price_bdt.toLocaleString()}{r.offer_price_bdt ? ` → ৳${r.offer_price_bdt.toLocaleString()}` : ''} / night · {r.capacity} guests
                  </div>
                </div>
                <div className="flex flex-col gap-1 items-end">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.active ? 'bg-forest-700 text-cream-100' : 'bg-slate-200 text-slate-600'}`}>{r.active ? 'live' : 'hidden'}</span>
                  {r.featured && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gold-600 text-cream-100">Featured</span>}
                </div>
              </div>
              <p className="mt-3 text-xs text-slate-warm line-clamp-2 flex-1">{r.description_en}</p>
              <div className="mt-4 flex gap-2">
                <button onClick={() => setEditing(r)} data-testid={`edit-room-${r.id}`}
                        className="flex-1 text-xs border border-forest-200 text-forest-700 py-2 rounded-md hover:bg-cream-200 inline-flex items-center justify-center gap-1.5">
                  <Pencil className="w-3 h-3"/> Edit
                </button>
                <button onClick={() => remove(r.id)} data-testid={`delete-room-${r.id}`}
                        className="text-xs border border-red-200 text-red-600 px-3 py-2 rounded-md hover:bg-red-50 inline-flex items-center gap-1.5">
                  <Trash2 className="w-3 h-3"/>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <RoomEditor room={editing} authHeaders={authHeaders} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function RoomEditor({ room, authHeaders, onClose, onSaved }) {
  const isNew = !room.id;
  const [form, setForm] = useState({
    name_en: room.name_en || "", name_bn: room.name_bn || "",
    description_en: room.description_en || "", description_bn: room.description_bn || "",
    price_bdt: room.price_bdt || 4000, offer_price_bdt: room.offer_price_bdt || "",
    capacity: room.capacity || 2,
    image_url: room.image_url || "",
    amenities: (room.amenities || []).join(", "),
    active: room.active ?? true,
    featured: room.featured ?? false,
  });
  const [saving, setSaving] = useState(false);

  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name_en: form.name_en, name_bn: form.name_bn,
      description_en: form.description_en, description_bn: form.description_bn,
      price_bdt: Number(form.price_bdt),
      offer_price_bdt: form.offer_price_bdt ? Number(form.offer_price_bdt) : null,
      capacity: Number(form.capacity),
      image_url: form.image_url,
      amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
      active: form.active, featured: form.featured,
    };
    try {
      if (isNew) await axios.post(`${API}/rooms`, payload, { headers: authHeaders });
      else await axios.put(`${API}/rooms/${room.id}`, payload, { headers: authHeaders });
      toast.success(isNew ? "Room created" : "Room updated");
      onSaved();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-forest-900/40 flex items-center justify-center p-4" data-testid="room-editor-modal">
      <form onSubmit={save} className="bg-white max-w-2xl w-full rounded-md max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-6 border-b border-forest-100">
          <div className="text-xl font-display text-forest-700">{isNew ? "New room" : "Edit room"}</div>
          <button type="button" onClick={onClose} data-testid="close-room-editor"><X className="w-5 h-5 text-slate-warm"/></button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-4">
          <Input label="Name (EN)" value={form.name_en} onChange={(v) => u('name_en', v)} testid="edit-name-en" />
          <Input label="Name (BN)" value={form.name_bn} onChange={(v) => u('name_bn', v)} testid="edit-name-bn" />
          <Input label="Standard Price (BDT)" type="number" value={form.price_bdt} onChange={(v) => u('price_bdt', v)} testid="edit-price" />
          <Input label="AI Offer Price (BDT, optional)" type="number" required={false} value={form.offer_price_bdt} onChange={(v) => u('offer_price_bdt', v)} testid="edit-offer" />
          <Input label="Capacity" type="number" value={form.capacity} onChange={(v) => u('capacity', v)} testid="edit-capacity" />
          <div className="col-span-1"><Input label="Image URL" value={form.image_url} onChange={(v) => u('image_url', v)} testid="edit-image" /></div>
          <div className="col-span-2"><TextArea label="Description (EN)" value={form.description_en} onChange={(v) => u('description_en', v)} testid="edit-desc-en" /></div>
          <div className="col-span-2"><TextArea label="Description (BN)" value={form.description_bn} onChange={(v) => u('description_bn', v)} testid="edit-desc-bn" /></div>
          <div className="col-span-2"><Input label="Amenities (comma separated)" value={form.amenities} onChange={(v) => u('amenities', v)} testid="edit-amenities" /></div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => u('active', e.target.checked)} data-testid="edit-active" />
            Active (visible on site)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.featured} onChange={(e) => u('featured', e.target.checked)} data-testid="edit-featured" />
            Featured suite (top of page)
          </label>
        </div>
        <div className="p-6 border-t border-forest-100 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm text-slate-warm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="save-room-btn"
                  className="bg-forest-700 text-cream-100 px-6 py-2.5 rounded-full text-sm hover:bg-forest-800 disabled:opacity-60 inline-flex items-center gap-2 border border-gold-600/40">
            {saving && <Loader2 className="w-4 h-4 animate-spin"/>} Save
          </button>
        </div>
      </form>
    </div>
  );
}

// Replace nested ternary with explicit lookup table
const statusPillClass = (status) => {
  if (status === 'new') return 'bg-forest-700 text-cream-100 border-forest-700';
  if (status === 'confirmed') return 'bg-gold-600 text-cream-100 border-gold-600';
  return 'bg-cream-100 text-forest-700 border-forest-100';
};

const Input = ({ label, value, onChange, type="text", testid, required=true }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-widest2 text-gold-600 mb-1.5">{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid}
           className="w-full bg-cream-100 border border-forest-100 px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gold-600/40 focus:border-gold-600" />
  </label>
);
const TextArea = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-widest2 text-gold-600 mb-1.5">{label}</span>
    <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} required data-testid={testid}
              className="w-full bg-cream-100 border border-forest-100 px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gold-600/40 focus:border-gold-600" />
  </label>
);
